import React, { useState, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import type { FormikProps } from "formik";
import type { SilaeFormData, SilaeCondition } from "@/types/silae";
import {
  Card,
  CardHeader,
  CardContent,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Filter, Plus, Trash2, AlertCircle } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "../ui/label";
import { ConditionItem } from "./ConditionItem";
import { getIn } from "formik";

interface SourceConditionsConfigProps {
  formik: FormikProps<SilaeFormData>;
  sourceFieldPrefix: string; // Ex: "ifBranch.bonuses.0.bases.0"
  totalSources: number; // Total des sources dans le même tableau (bases ou tauxs)
  sourceIndex: number;
}

/**
 * Composant pour gérer les conditions d'une source
 * Si totalSources > 1, les conditions sont obligatoires
 * Sinon, les conditions sont optionnelles
 */
export const SourceConditionsConfig: React.FC<SourceConditionsConfigProps> = ({
  formik,
  sourceFieldPrefix,
  totalSources,
  sourceIndex,
}) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState("condition-0");

  // Récupérer les valeurs de la source
  const source = useMemo(
    () => getIn(formik.values, sourceFieldPrefix),
    [formik.values, sourceFieldPrefix],
  );

  const sourceConditions = useMemo(
    () => source?.conditions || [],
    [source?.conditions],
  );

  const hasConditions = useMemo(
    () => source?.hasConditions || false,
    [source?.hasConditions],
  );

  // Forcer les conditions si plusieurs sources
  const isConditionsRequired = totalSources > 1;
  const effectiveHasConditions = isConditionsRequired || hasConditions;

  // Récupérer les erreurs de validation
  const conditionsErrors = useMemo(
    () => getIn(formik.errors, `${sourceFieldPrefix}.conditions`),
    [formik.errors, sourceFieldPrefix],
  );

  const hasConditionsErrors =
    conditionsErrors &&
    (Array.isArray(conditionsErrors) ? conditionsErrors.length > 0 : true);

  const toggleSourceConditions = useCallback(
    (enabled: boolean) => {
      // Empêcher la désactivation si plusieurs sources
      if (isConditionsRequired && !enabled) {
        return;
      }

      formik.setFieldValue(`${sourceFieldPrefix}.hasConditions`, enabled);
      if (!enabled) {
        formik.setFieldValue(`${sourceFieldPrefix}.conditions`, []);
        formik.setFieldValue(`${sourceFieldPrefix}.conditionsOperators`, []);
        formik.setFieldValue(`${sourceFieldPrefix}.elseSource`, null);
        formik.setFieldTouched(`${sourceFieldPrefix}.hasConditions`, true);
        formik.setFieldTouched(`${sourceFieldPrefix}.conditions`, true);
        formik.setFieldTouched(
          `${sourceFieldPrefix}.conditionsOperators`,
          true,
        );
        formik.setFieldTouched(`${sourceFieldPrefix}.elseSource`, true);
        requestAnimationFrame(() => {
          formik.setFieldTouched(`${sourceFieldPrefix}.hasConditions`, true);
          formik.setFieldTouched(`${sourceFieldPrefix}.conditions`, true);
          formik.setFieldTouched(
            `${sourceFieldPrefix}.conditionsOperators`,
            true,
          );
          formik.setFieldTouched(`${sourceFieldPrefix}.elseSource`, true);
        });
      } else {
        if (sourceConditions.length === 0) {
          const newCondition: SilaeCondition = {
            id: `condition_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            type: "VAR_SALARY_BASE",
            silaeVariable: "bul.salairedebase",
            conditionOperator: ">=",
            conditionValue: "",
          };
          formik.setFieldValue(`${sourceFieldPrefix}.conditions`, [
            newCondition,
          ]);
        }

        // Créer automatiquement un elseSource vide si les conditions sont activées
        // if (!source?.elseSource) {
        //   formik.setFieldValue(
        //     `${sourceFieldPrefix}.elseSource`,
        //     createEmptySource()
        //   );
        // }
      }
    },
    [
      formik,
      sourceFieldPrefix,
      sourceConditions.length,
      isConditionsRequired,
      // source?.elseSource,
    ],
  );

  const addSourceCondition = useCallback(() => {
    const newCondition: SilaeCondition = {
      id: `condition_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      type: "VAR_SALARY_BASE",
      silaeVariable: "bul.salairedebase",
      conditionOperator: ">=",
      conditionValue: "",
      conditionPercentage: "100",
    };
    const updatedConditions = [...sourceConditions, newCondition];

    // Ajouter l'opérateur "AND" par défaut si on a maintenant plusieurs conditions
    const currentOps = (source?.conditionsOperators || []) as string[];
    if (updatedConditions.length > 1) {
      const updatedOps = [...currentOps, "AND"];
      formik.setFieldValue(
        `${sourceFieldPrefix}.conditionsOperators`,
        updatedOps,
      );
    }

    formik.setFieldValue(`${sourceFieldPrefix}.conditions`, updatedConditions);
    setActiveTab(`condition-${updatedConditions.length - 1}`);
  }, [
    sourceConditions,
    formik,
    sourceFieldPrefix,
    source?.conditionsOperators,
  ]);

  const removeSourceCondition = useCallback(
    (index: number) => {
      if (sourceConditions.length <= 1) return;
      const updatedConditions = sourceConditions.filter(
        (_: SilaeCondition, i: number) => i !== index,
      );
      formik.setFieldValue(
        `${sourceFieldPrefix}.conditions`,
        updatedConditions,
      );

      // Ajuster les opérateurs si nécessaire
      const ops = (source?.conditionsOperators || []) as string[];
      if (ops.length > 0 && updatedConditions.length > 1) {
        const updatedOps: string[] = [];
        for (let i = 0; i < index && i < ops.length; i++) {
          updatedOps.push(ops[i] || "AND");
        }
        for (let i = index + 1; i < ops.length; i++) {
          updatedOps.push(ops[i] || "AND");
        }
        while (updatedOps.length < updatedConditions.length - 1) {
          updatedOps.push("AND");
        }
        formik.setFieldValue(
          `${sourceFieldPrefix}.conditionsOperators`,
          updatedOps,
        );
      } else if (updatedConditions.length === 1) {
        formik.setFieldValue(`${sourceFieldPrefix}.conditionsOperators`, []);
      }

      // Ajuster l'onglet actif
      if (activeTab === `condition-${index}`) {
        const newIndex = Math.max(0, index - 1);
        setActiveTab(`condition-${newIndex}`);
      } else {
        const currentTabIndex = parseInt(activeTab.split("-")[1] || "0");
        if (currentTabIndex > index) {
          setActiveTab(`condition-${currentTabIndex - 1}`);
        }
      }
    },
    [
      sourceConditions,
      formik,
      sourceFieldPrefix,
      source?.conditionsOperators,
      activeTab,
    ],
  );

  return (
    <Card
      className={`bg-muted/30 dark:bg-muted/10 border-dashed p-4 ${
        hasConditionsErrors ? "border-red-500" : ""
      }`}
    >
      <CardHeader className="flex mb-0 items-center p-4 rounded-lg bg-muted/50 justify-between">
        <div className="flex flex-col justify-between">
          <div className="flex items-center gap-2">
            <Filter className="h-5 w-5 text-muted-foreground" />
            <CardTitle className="text-base">
              {t("sourceConditions.title")}
            </CardTitle>
          </div>
          <CardDescription className="text-sm">
            {isConditionsRequired
              ? t("sourceConditions.requiredForMultiple")
              : t("sourceConditions.description")}
          </CardDescription>
        </div>
        <Switch
          id={`hasSourceConditions-${sourceIndex}`}
          checked={effectiveHasConditions}
          onCheckedChange={toggleSourceConditions}
          disabled={isConditionsRequired}
        />
      </CardHeader>

      {effectiveHasConditions && (
        <CardContent className="space-y-4 px-0">
          {/* Affichage des erreurs générales de validation */}
          {hasConditionsErrors && (
            <div className="space-y-2">
              <div className="p-3 bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800 rounded-lg">
                <div className="flex items-start gap-2">
                  <AlertCircle className="h-4 w-4 text-red-600 dark:text-red-400 mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    {typeof conditionsErrors === "string" ? (
                      <p className="text-sm text-red-600 dark:text-red-400">
                        {conditionsErrors}
                      </p>
                    ) : Array.isArray(conditionsErrors) ? (
                      <div className="space-y-1">
                        {conditionsErrors.map(
                          (error: unknown, index: number) => {
                            if (!error) return null;
                            if (typeof error === "string") {
                              return (
                                <p
                                  key={index}
                                  className="text-sm text-red-600 dark:text-red-400"
                                >
                                  {t("advancedConditions.condition")}{" "}
                                  {index + 1}: {error}
                                </p>
                              );
                            }
                            if (typeof error === "object") {
                              return (
                                <div key={index} className="space-y-1">
                                  {(
                                    Object.entries(
                                      error as Record<string, unknown>,
                                    ) as [string, unknown][]
                                  ).map(([key, err]: [string, unknown]) => {
                                    if (!err) return null;
                                    return (
                                      <p
                                        key={key}
                                        className="text-sm text-red-600 dark:text-red-400"
                                      >
                                        {t("advancedConditions.condition")}{" "}
                                        {index + 1}.{key}: {String(err)}
                                      </p>
                                    );
                                  })}
                                </div>
                              );
                            }
                            return null;
                          },
                        )}
                      </div>
                    ) : (
                      <p className="text-sm text-red-600 dark:text-red-400">
                        {String(conditionsErrors)}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Information sur les conditions multiples */}
          {sourceConditions.length > 0 && (
            <div className="flex items-center gap-2 p-3 bg-blue-50 dark:bg-blue-950/20 rounded-lg">
              <div className="text-sm text-blue-900 dark:text-blue-100">
                💡 {t("sourceConditions.multipleInfo")}
              </div>
            </div>
          )}

          {/* Bouton pour ajouter une condition et opérateurs logiques */}
          <div className="flex items-center justify-between gap-2">
            {sourceConditions.length > 1 && (
              <div className="space-y-2 border-b pb-4">
                <Label>{t("advancedConditions.logicalOperator")}</Label>
                <div className="text-sm text-muted-foreground mb-2">
                  {t("advancedConditions.operatorBetweenConditions")}
                </div>
                <div className="flex flex-wrap gap-4 items-center">
                  {sourceConditions
                    .slice(0, -1)
                    .map((_: SilaeCondition, index: number) => {
                      const ops = (source?.conditionsOperators ||
                        []) as string[];
                      const current = ops[index] || "AND";

                      return (
                        <div key={index} className="flex items-center gap-2">
                          <Badge variant="outline" className="text-xs">
                            {t("advancedConditions.condition")} {index + 1}
                          </Badge>
                          <Select
                            value={current}
                            onValueChange={(value) => {
                              const next = [...ops];
                              while (next.length < sourceConditions.length - 1)
                                next.push("AND");
                              next[index] = value;
                              formik.setFieldValue(
                                `${sourceFieldPrefix}.conditionsOperators`,
                                next,
                              );
                            }}
                          >
                            <SelectTrigger className="w-fit min-w-[80px]">
                              <SelectValue>
                                {current === "AND" ? (
                                  <Badge variant="secondary">
                                    {t(
                                      "advancedConditions.operators.and.label",
                                    )}
                                  </Badge>
                                ) : (
                                  <Badge variant="outline">
                                    {t("advancedConditions.operators.or.label")}
                                  </Badge>
                                )}
                              </SelectValue>
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="AND">
                                <div className="flex items-center gap-2">
                                  <Badge variant="secondary">
                                    {t(
                                      "advancedConditions.operators.and.label",
                                    )}
                                  </Badge>
                                  <span>
                                    {t(
                                      "advancedConditions.operators.and.description",
                                    )}
                                  </span>
                                </div>
                              </SelectItem>
                              <SelectItem value="OR">
                                <div className="flex items-center gap-2">
                                  <Badge variant="outline">
                                    {t("advancedConditions.operators.or.label")}
                                  </Badge>
                                  <span>
                                    {t(
                                      "advancedConditions.operators.or.description",
                                    )}
                                  </span>
                                </div>
                              </SelectItem>
                            </SelectContent>
                          </Select>
                          <Badge variant="outline" className="text-xs">
                            {t("advancedConditions.condition")} {index + 2}
                          </Badge>
                        </div>
                      );
                    })}
                </div>
              </div>
            )}
            <Button
              type="button"
              variant="outline"
              onClick={addSourceCondition}
              className="w-fit"
              disabled={sourceConditions.length >= 5}
            >
              <Plus className="h-4 w-4 mr-2" />
              {t("sourceConditions.addCondition")}
            </Button>
          </div>

          {/* Liste des conditions avec système de tabs si plusieurs */}
          {sourceConditions.length === 1 ? (
            // Une seule condition : affichage direct sans tabs
            <ConditionItem
              formik={
                {
                  ...formik,
                  values: {
                    ...formik.values,
                    conditions: sourceConditions,
                  },
                  errors: {
                    ...formik.errors,
                    conditions: getIn(
                      formik.errors,
                      `${sourceFieldPrefix}.conditions`,
                    ),
                  },
                  touched: {
                    ...formik.touched,
                    conditions: getIn(
                      formik.touched,
                      `${sourceFieldPrefix}.conditions`,
                    ),
                  },
                  setFieldValue: (field: string, value: unknown) => {
                    if (
                      field.startsWith("conditions[") ||
                      field.startsWith("conditions.")
                    ) {
                      const cleanField = field.replace(/^conditions[[.]/, "");
                      formik.setFieldValue(
                        `${sourceFieldPrefix}.conditions.${cleanField}`,
                        value,
                      );
                    } else {
                      formik.setFieldValue(field, value);
                    }
                  },
                  setFieldTouched: (field: string, touched?: boolean) => {
                    if (
                      field.startsWith("conditions[") ||
                      field.startsWith("conditions.")
                    ) {
                      const cleanField = field.replace(/^conditions[[.]/, "");
                      formik.setFieldTouched(
                        `${sourceFieldPrefix}.conditions.${cleanField}`,
                        touched,
                      );
                    } else {
                      formik.setFieldTouched(field, touched);
                    }
                  },
                } as FormikProps<SilaeFormData>
              }
              conditionIndex={0}
              onDelete={() => removeSourceCondition(0)}
              canDelete={false}
            />
          ) : (
            // Plusieurs conditions : utiliser les tabs
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="w-full justify-start overflow-x-auto flex-wrap h-auto">
                {sourceConditions.map(
                  (condition: SilaeCondition, index: number) => (
                    <div key={condition.id} className="flex items-center gap-1">
                      <TabsTrigger
                        value={`condition-${index}`}
                        className="flex items-center gap-2"
                      >
                        <Filter className="h-3 w-3" />
                        {t("advancedConditions.condition")} {index + 1}
                        {sourceConditions.length > 1 && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              removeSourceCondition(index);
                            }}
                            className="h-4 w-4 p-0 ml-1 hover:bg-destructive/10 hover:text-destructive"
                          >
                            <Trash2 className="h-2 w-2" />
                          </Button>
                        )}
                      </TabsTrigger>
                    </div>
                  ),
                )}
              </TabsList>

              {sourceConditions.map((_: SilaeCondition, index: number) => (
                <TabsContent key={index} value={`condition-${index}`}>
                  <ConditionItem
                    formik={
                      {
                        ...formik,
                        values: {
                          ...formik.values,
                          conditions: sourceConditions,
                        },
                        errors: {
                          ...formik.errors,
                          conditions: getIn(
                            formik.errors,
                            `${sourceFieldPrefix}.conditions`,
                          ),
                        },
                        touched: {
                          ...formik.touched,
                          conditions: getIn(
                            formik.touched,
                            `${sourceFieldPrefix}.conditions`,
                          ),
                        },
                        setFieldValue: (field: string, value: unknown) => {
                          if (
                            field.startsWith("conditions[") ||
                            field.startsWith("conditions.")
                          ) {
                            const cleanField = field.replace(
                              /^conditions[[.]/,
                              "",
                            );
                            formik.setFieldValue(
                              `${sourceFieldPrefix}.conditions.${cleanField}`,
                              value,
                            );
                          } else {
                            formik.setFieldValue(field, value);
                          }
                        },
                        setFieldTouched: (field: string, touched?: boolean) => {
                          if (
                            field.startsWith("conditions[") ||
                            field.startsWith("conditions.")
                          ) {
                            const cleanField = field.replace(
                              /^conditions[[.]/,
                              "",
                            );
                            formik.setFieldTouched(
                              `${sourceFieldPrefix}.conditions.${cleanField}`,
                              touched,
                            );
                          } else {
                            formik.setFieldTouched(field, touched);
                          }
                        },
                      } as FormikProps<SilaeFormData>
                    }
                    conditionIndex={index}
                    onDelete={() => removeSourceCondition(index)}
                    canDelete={sourceConditions.length > 1}
                  />
                </TabsContent>
              ))}
            </Tabs>
          )}
        </CardContent>
      )}
    </Card>
  );
};
